import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Grid,
    Slide,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    useTheme,
    InputAdornment,
    Tooltip,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    useMediaQuery,
    FormControlLabel,
    Switch,
    Autocomplete,
    CircularProgress
} from '@mui/material';
import {
    Delete,
    Search,
    Add as AddIcon,
    Close as CloseIcon,
    FilterList as FilterIcon,
    Refresh as RefreshIcon,
    Visibility as ViewIcon,
    CheckCircle,
    Cancel,
    School,
    CalendarToday,
    Update,
    Business,
    ExitToApp,
    HourglassEmpty,
    CheckCircleOutlineOutlined,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APISupervisorInstitutionList,
    APISupervisorInstitutionStore,
    APISupervisorInstitutionUpdate,
    APISupervisorInstitutionDelete,
    APIUniversityNames
} from '../../apiServices/APIs';
import { 
    formatCreatedAtDate, 
    recordsCount, 
    recordsPerPage, 
    getUserDetails 
} from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import ErrorState from '../../components/ErrorState';
import LoadingState from '../../components/LoadingState';
import SupervisorBar from '../../components/SupervisorBar';
import { FullWidthContainer } from '../../components/FullWidthContainer';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const MyInstitution = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    const [loading, setLoading] = useState(false);
    const [loadingUniversities, setLoadingUniversities] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [updateId, setUpdateId] = useState('');
    const [postStatus, setPostStatus] = useState('ADD');
    const [pageState, setPageState] = useState({
        pageData: []
    });
    const [universities, setUniversities] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [universitySearchInput, setUniversitySearchInput] = useState('');

    // Form selection state
    const [formData, setFormData] = useState({
        university_id: '',
        university_name: '',
        is_active: true,
        is_left: false
    });
    
    const [filters, setFilters] = useState({
        is_approved: '',
        is_active: '',
        is_left: ''
    });
    
    const [errors, setErrors] = useState({
        university_id: false
    });
    
    const [filterOptions, setFilterOptions] = useState(false);
    const [openAdd, setOpenAdd] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    
    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        fetchData();
        fetchUniversityNames();
        
        return () => {
            isMounted.current = false;
        };
    }, [showToast]);
    
    // Fetch user affiliations
    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const supervisorId = getUserDetails("Id");
            const result = await apiService.list(`${APISupervisorInstitutionList}?supervisor_id=${supervisorId}`);
            if (result.success && isMounted.current) {
                const affiliations = Array.isArray(result.data) ? result.data : [];
                setPageState({ pageData: affiliations });
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError("Failed to load affiliations");
            console.error("Fetch error:", error);
        } finally {
            if (isMounted.current) setLoading(false);
        }
    }, []);

    // Fetch master list of universities for Autocomplete
    const fetchUniversityNames = async () => {
        setLoadingUniversities(true);
        try {
            const result = await apiService.list(APIUniversityNames);
            if (result.success && isMounted.current) {
                const data = Array.isArray(result.data) ? result.data : [];
                setUniversities(data);
            }
        } catch (error) {
            console.error("Master list load error:", error);
        } finally {
            if (isMounted.current) setLoadingUniversities(false);
        }
    };
    
    // Client side filtering for visual searches
    const filteredData = useMemo(() => {
        return pageState.pageData.filter(record => {
            const matchesSearch = searchTerm === '' ||
                (record.university_name?.toLowerCase() || '').includes(searchTerm.toLowerCase());
            
            const matchesActive = filters.is_active === '' || 
                (filters.is_active === 'active' && record.is_active) ||
                (filters.is_active === 'inactive' && !record.is_active);

            const matchesApproved = filters.is_approved === '' || 
                (filters.is_approved === 'approved' && record.is_approved) ||
                (filters.is_approved === 'pending' && !record.is_approved);

            const matchesLeft = filters.is_left === '' || 
                (filters.is_left === 'left' && record.is_left) ||
                (filters.is_left === 'current' && !record.is_left);
            
            return matchesSearch && matchesActive && matchesApproved && matchesLeft;
        });
    }, [pageState.pageData, searchTerm, filters]);
    
    const handleChangePage = useCallback((event, newPage) => setPage(newPage), []);
    const handleChangeRowsPerPage = useCallback((event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    }, []);
    
    const handleFilterOptions = useCallback(() => setFilterOptions(true), []);
    const handleCloseOptions = useCallback(() => setFilterOptions(false), []);
    
    const handleOpenAdd = useCallback(() => {
        setPostStatus("ADD");
        setFormData({
            university_id: '',
            university_name: '',
            is_active: true,
            is_left: false
        });
        setUniversitySearchInput('');
        setErrors({ university_id: false });
        setOpenAdd(true);
    }, []);
    
    const handleCloseAdd = useCallback(() => setOpenAdd(false), []);
    
    const handleOpenView = useCallback((record) => {
        setSelectedRecord(record);
        setOpenView(true);
    }, []);
    
    const handleCloseView = useCallback(() => setOpenView(false), []);
    
    const handleOpenDelete = useCallback((record) => {
        setSelectedRecord(record);
        setUpdateId(record.id);
        setPostStatus("DELETE");
        setOpenDelete(true);
    }, []);
    
    const handleCloseDelete = useCallback(() => setOpenDelete(false), []);

    const handleUniversitySelect = (event, selectedUniversity) => {
        if (selectedUniversity) {
            setFormData(prev => ({
                ...prev,
                university_id: selectedUniversity.id,
                university_name: selectedUniversity.name
            }));
            setErrors(prev => ({ ...prev, university_id: false }));
        } else {
            setFormData(prev => ({
                ...prev,
                university_id: '',
                university_name: ''
            }));
        }
    };
    
    const handleStatusToggle = async (record, field) => {
        const formDataToSend = new FormData();
        formDataToSend.append('updateId', record.id);
        formDataToSend.append(field, !record[field] ? '1' : '0');

        try {
            const result = await apiService.update(APISupervisorInstitutionUpdate, formDataToSend);
            if (result && result.success) {
                showToast("Success", "Affiliation details updated", "success");
                fetchData();
            } else {
                showToast("Error", result?.message || "Operation failed", "error");
            }
        } catch (error) {
            showToast("Error", "Internal connection error", "error");
        }
    };
    
    const handleFilterChange = useCallback((e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    }, []);
    
    const clearFilters = useCallback(() => {
        setFilters({
            is_approved: '',
            is_active: '',
            is_left: ''
        });
        setSearchTerm('');
        handleCloseOptions();
    }, [handleCloseOptions]);
    
    const validateForm = useCallback(() => {
        if (!formData.university_id) {
            showToast("Validation Error", "Please select a university from the search options.", "warning");
            setErrors({ university_id: true });
            return false;
        }
        return true;
    }, [formData.university_id, showToast]);
    
    const handleSubmit = useCallback(async (e) => {
        if (e) e.preventDefault();
        
        if (postStatus !== "DELETE" && !validateForm()) return;
        
        const formDataToSend = new FormData();
        formDataToSend.append('supervisor_id', getUserDetails("Id"));
        
        if (postStatus === "ADD") {
            formDataToSend.append('university_id', formData.university_id);
        } else if (postStatus === "DELETE") {
            formDataToSend.append('updateId', updateId);
        }
        
        try {
            let result;
            if (postStatus === "ADD") {
                result = await apiService.create(APISupervisorInstitutionStore, formDataToSend);
            } else {
                result = await apiService.delete(APISupervisorInstitutionDelete, formDataToSend);
            }
            
            if (result && result.success && isMounted.current) {
                showToast("Success", result.message, "success");
                setOpenAdd(false);
                setOpenDelete(false);
                fetchData();
            } else if (result && !result.success) {
                showToast("Error", result.message || "Operation failed", "error");
            }
        } catch (error) {
            showToast("Error", error.response?.data?.message || "Network request failed", "error");
        }
    }, [postStatus, validateForm, formData, updateId, showToast, fetchData]);

    if (loading) {
        return <LoadingState message="Loading Your Universities..." size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchData} title="Unable to Load Affiliations" fullPage />;
    }

    return (
        <>
            <FullWidthContainer>
                <Box sx={{ backgroundColor: 'white', borderRadius: 3, mb: 3 }}>
                    <SupervisorBar />
                    <Paper elevation={2} sx={{ 
                        p: { xs: 2, sm: 3 }, 
                        borderRadius: 3,
                        backgroundColor: 'white',
                    }}>
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 2, 
                            flexWrap: 'wrap',
                            flexDirection: { xs: 'column', sm: 'row' }
                        }}>
                            <Typography sx={{
                                fontWeight: 'bold',
                                color: theme.palette.primary.main,
                                flexShrink: 0,
                                fontSize: { xs: '1rem', sm: '1.25rem' }
                            }}>
                                Associated Academic institutions ({recordsCount(pageState.pageData.length)})
                            </Typography>

                            <Box sx={{ 
                                display: 'flex', 
                                gap: 1, 
                                flexShrink: 0,
                                width: { xs: '100%', sm: 'auto' },
                                justifyContent: { xs: 'center', sm: 'flex-start' }
                            }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<AddIcon />}
                                    onClick={handleOpenAdd}
                                    sx={{ textTransform: 'none', borderRadius: '8px' }}
                                    size={isMobile ? "small" : "medium"}
                                >
                                    Add Institution
                                </Button>

                                <Button
                                    variant="contained"
                                    color="secondary"
                                    startIcon={<FilterIcon />}
                                    onClick={handleFilterOptions}
                                    sx={{ textTransform: 'none', borderRadius: '8px' }}
                                    size={isMobile ? "small" : "medium"}
                                >
                                    Filter Views
                                </Button>
                                
                                <Button
                                    variant="outlined"
                                    color="info"
                                    startIcon={<RefreshIcon />}
                                    onClick={fetchData}
                                    sx={{ textTransform: 'none', borderRadius: '8px' }}
                                    size={isMobile ? "small" : "medium"}
                                >
                                    Refresh
                                </Button>
                            </Box>

                            <Paper sx={{
                                width: { xs: '100%', sm: '300px' },
                                p: '2px 8px',
                                display: 'flex',
                                alignItems: 'center',
                                marginLeft: { xs: 0, sm: 'auto' },
                                borderRadius: '8px',
                                backgroundColor: theme.palette.grey[50],
                            }}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    placeholder="Search affiliated university..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: <Search sx={{ mr: 1, color: theme.palette.grey[600] }} />,
                                        sx: {
                                            '& fieldset': { border: 'none' },
                                            fontSize: '0.875rem',
                                        }
                                    }}
                                    size="small"
                                />
                            </Paper>
                        </Box>

                        {/* Active Filter Indicators */}
                        {(filters.is_active || filters.is_approved || filters.is_left) && (
                            <Box sx={{ 
                                display: 'flex', 
                                gap: 1, 
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                mt: 2,
                                pt: 2,
                                borderTop: '1px solid',
                                borderColor: 'divider'
                            }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mr: 1, fontSize: '0.75rem' }}>
                                    Filters:
                                </Typography>
                                {filters.is_active && (
                                    <Chip
                                        label={`Status: ${filters.is_active === 'active' ? 'Active' : 'Inactive'}`}
                                        onDelete={() => setFilters(prev => ({ ...prev, is_active: '' }))}
                                        color="primary"
                                        variant="outlined"
                                        size="small"
                                    />
                                )}
                                {filters.is_approved && (
                                    <Chip
                                        label={`Verification: ${filters.is_approved === 'approved' ? 'Approved' : 'Pending'}`}
                                        onDelete={() => setFilters(prev => ({ ...prev, is_approved: '' }))}
                                        color="info"
                                        variant="outlined"
                                        size="small"
                                    />
                                )}
                                {filters.is_left && (
                                    <Chip
                                        label={`Tenure: ${filters.is_left === 'left' ? 'Left' : 'Current'}`}
                                        onDelete={() => setFilters(prev => ({ ...prev, is_left: '' }))}
                                        color="warning"
                                        variant="outlined"
                                        size="small"
                                    />
                                )}
                                <Button size="small" onClick={clearFilters} startIcon={<CloseIcon />}>
                                    Clear All
                                </Button>
                            </Box>
                        )}
                    </Paper>
                </Box>
                
                <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'auto', backgroundColor: 'white' }}>
                        <TableContainer>
                            <Table sx={{ minWidth: 650 }}>
                                <TableHead>
                                    <TableRow sx={{
                                        backgroundColor: theme.palette.primary.main,
                                        '& th': {
                                            color: 'white',
                                            fontWeight: 'bold',
                                            fontSize: { xs: '0.75rem', sm: '0.85rem' }
                                        }
                                    }}>
                                        <TableCell>#</TableCell>
                                        <TableCell>University Name</TableCell>
                                        <TableCell>Verification</TableCell>
                                        <TableCell>Status (Active)</TableCell>
                                        <TableCell>Current Tenure</TableCell>
                                        <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((record, index) => (
                                        <TableRow
                                            key={record.id}
                                            hover
                                            sx={{ '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover } }}
                                        >
                                            <TableCell>
                                                <Chip label={page * rowsPerPage + index + 1} color="primary" size="small" />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <School color="primary" fontSize="small" />
                                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                        {record.university_name}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={record.is_approved ? <CheckCircleOutlineOutlined /> : <HourglassEmpty />}
                                                    label={record.is_approved ? "Approved" : "Pending Approval"}
                                                    color={record.is_approved ? "success" : "warning"}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            size="small"
                                                            checked={record.is_active}
                                                            onChange={() => handleStatusToggle(record, 'is_active')}
                                                            color="success"
                                                        />
                                                    }
                                                    label={record.is_active ? "Active" : "Inactive"}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            size="small"
                                                            checked={record.is_left}
                                                            onChange={() => handleStatusToggle(record, 'is_left')}
                                                            color="error"
                                                        />
                                                    }
                                                    label={record.is_left ? "Left Institution" : "In Service"}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                                    <Tooltip title="View Meta Information">
                                                        <IconButton onClick={() => handleOpenView(record)} color="primary" size="small">
                                                            <ViewIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete Affiliation">
                                                        <IconButton onClick={() => handleOpenDelete(record)} color="error" size="small">
                                                            <Delete fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            component="div"
                            count={filteredData.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />

                        {filteredData.length === 0 && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, color: 'text.secondary' }}>
                                <School sx={{ fontSize: 60, mb: 2 }} />
                                <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                                    No university associations found
                                </Typography>
                                <Typography variant="body2">Try adjusting your filters or search terms</Typography>
                            </Box>
                        )}
                    </Paper>
                </motion.div>

                {/* Filter Settings Dialog */}
                <Dialog
                    open={filterOptions}
                    TransitionComponent={Transition}
                    fullWidth
                    maxWidth="sm"
                    onClose={handleCloseOptions}
                >
                    <DialogTitle sx={{ textAlign: 'center' }}>Affiliation Filters</DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 1 }}>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<RefreshIcon />}
                                onClick={clearFilters}
                                fullWidth
                            >
                                Reset All Filters
                            </Button>

                            <Divider>Filter By Details</Divider>

                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Verification Status</InputLabel>
                                        <Select
                                            name="is_approved"
                                            value={filters.is_approved}
                                            onChange={handleFilterChange}
                                            label="Verification Status"
                                        >
                                            <MenuItem value="">All Statuses</MenuItem>
                                            <MenuItem value="approved">Approved Associations</MenuItem>
                                            <MenuItem value="pending">Pending Admin Review</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Visibility Active State</InputLabel>
                                        <Select
                                            name="is_active"
                                            value={filters.is_active}
                                            onChange={handleFilterChange}
                                            label="Visibility Active State"
                                        >
                                            <MenuItem value="">Show All Active/Inactive</MenuItem>
                                            <MenuItem value="active">Active Only</MenuItem>
                                            <MenuItem value="inactive">Hidden Only</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Employment Tenancy</InputLabel>
                                        <Select
                                            name="is_left"
                                            value={filters.is_left}
                                            onChange={handleFilterChange}
                                            label="Employment Tenancy"
                                        >
                                            <MenuItem value="">Show All Employed/Left</MenuItem>
                                            <MenuItem value="current">Current Staff</MenuItem>
                                            <MenuItem value="left">Former Staff (Marked Left)</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: 3 }}>
                        <Button color="error" onClick={handleCloseOptions} variant="contained">Close</Button>
                        <Button onClick={handleCloseOptions} color="primary" variant="contained">Apply</Button>
                    </DialogActions>
                </Dialog>

                {/* Autocomplete Add Dialog */}
                <Dialog
                    open={openAdd}
                    onClose={handleCloseAdd}
                    TransitionComponent={Transition}
                    fullWidth
                    maxWidth="sm"
                    disableEscapeKeyDown
                >
                    <DialogTitle sx={{ textAlign: 'center' }}>
                        <b>Link New University Association</b>
                    </DialogTitle>
                    <DialogContent>
                        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                            <Autocomplete
                                id="university-search-select"
                                options={universities}
                                loading={loadingUniversities}
                                value={universities.find(u => u.id === formData.university_id) || null}
                                onChange={handleUniversitySelect}
                                inputValue={universitySearchInput}
                                onInputChange={(event, newInputValue) => {
                                    setUniversitySearchInput(newInputValue);
                                }}
                                getOptionLabel={(option) => `${option.name} ${option.code ? `(${option.code})` : ''}`}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select University / Institution *"
                                        required
                                        error={errors.university_id}
                                        helperText={errors.university_id ? "University selection is required" : "Search to connect your institution"}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start">
                                                        <School color="action" />
                                                    </InputAdornment>
                                                    {params.InputProps?.startAdornment}
                                                </>
                                            ),
                                            endAdornment: (
                                                <>
                                                    {loadingUniversities && <CircularProgress color="inherit" size={20} />}
                                                    {params.InputProps?.endAdornment}
                                                </>
                                            )
                                        }}
                                    />
                                )}
                            />

                            <DialogActions sx={{ mt: 4, px: 0 }}>
                                <Button color="error" onClick={handleCloseAdd} variant="contained">
                                    Cancel
                                </Button>
                                <Button type="submit" color="primary" variant="contained">
                                    Link University
                                </Button>
                            </DialogActions>
                        </Box>
                    </DialogContent>
                </Dialog>

                {/* View Details Dialog */}
                <Dialog open={openView} onClose={handleCloseView} TransitionComponent={Transition} fullWidth maxWidth="sm">
                    <DialogTitle sx={{ textAlign: 'center' }}>
                        <b>Institution Affiliation Metadata</b>
                    </DialogTitle>
                    <DialogContent>
                        {selectedRecord && (
                            <Box sx={{ p: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                                    <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 'bold' }}>
                                        Record #{selectedRecord.id}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Chip
                                            label={selectedRecord.is_approved ? "Approved" : "Pending Approval"}
                                            color={selectedRecord.is_approved ? "success" : "warning"}
                                            size="small"
                                        />
                                        <Chip
                                            label={selectedRecord.is_left ? "Left" : "In Service"}
                                            color={selectedRecord.is_left ? "error" : "success"}
                                            size="small"
                                        />
                                    </Box>
                                </Box>

                                <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <School color="secondary" fontSize="small"/>
                                    <b>Name:</b> {selectedRecord.university_name}
                                </Typography>

                                <Divider sx={{ my: 2 }} />

                                <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    Affiliation Timeline
                                </Typography>
                                <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <CalendarToday color="info" fontSize="small"/>
                                    <b>Requested On:</b> {formatCreatedAtDate(selectedRecord.created_at)}
                                </Typography>
                                <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Update color="info" fontSize="small"/>
                                    <b>Last Activity:</b> {formatCreatedAtDate(selectedRecord.updated_at)}
                                </Typography>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: 3 }}>
                        <Button color="error" onClick={handleCloseView} variant="contained">Close</Button>
                    </DialogActions>
                </Dialog>

                {/* Delete Association Dialog */}
                <Dialog open={openDelete} onClose={handleCloseDelete} TransitionComponent={Transition}>
                    <DialogTitle sx={{ textAlign: 'center' }}>Remove University Association</DialogTitle>
                    <DialogContent>
                        <Typography sx={{ textAlign: 'center', mb: 2 }}>
                            Are you sure you want to remove your affiliation with this university?
                        </Typography>
                        {selectedRecord && (
                            <Box sx={{ textAlign: 'center', p: 1, backgroundColor: theme.palette.grey[50], borderRadius: 2 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                    {selectedRecord.university_name}
                                </Typography>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: 3 }}>
                        <Button color="error" onClick={handleCloseDelete} variant="contained">Cancel</Button>
                        <Button onClick={handleSubmit} color="primary" variant="contained">Confirm Removal</Button>
                    </DialogActions>
                </Dialog>
            </FullWidthContainer>
        </>
    );
};

export default MyInstitution;